import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Shield, Users, Loader2, CheckCircle2, XCircle, Eye, EyeOff } from 'lucide-react';
import { AuthContext } from '../App';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ROLE_CONFIG = {
  admin: { label: 'Admin', color: '#8B5CF6', icon: Shield },
  member: { label: 'Member', color: '#00A3A1', icon: Users },
};

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verify = async () => {
      try {
        const res = await axios.get(`${API}/invitations/verify/${token}`);
        setInvite(res.data);
        setName(res.data.name || '');
      } catch (e) {
        setError(e.response?.data?.detail || 'This invitation is invalid or has expired.');
      }
      setLoading(false);
    };
    verify();
  }, [token]);

  const handleAccept = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/invitations/accept/${token}`, { name: name.trim(), password });
      const { access_token, user } = res.data || {};
      if (access_token && user) {
        // Sign the new member straight in — no second login step needed.
        login(access_token, user);
        // Flag so the in-app Welcome Tour fires once on the dashboard.
        try { sessionStorage.setItem('simplly_show_welcome_tour', '1'); } catch {}
        toast.success(`Welcome to ${invite?.organization?.name || 'the team'}!`);
        // Brief success screen before pushing them into the app
        setSuccess(true);
        setTimeout(() => navigate('/dashboard', { replace: true }), 1200);
      } else {
        // Backend returned old-shape response — fall back to manual login.
        setSuccess(true);
        toast.success('Account created successfully!');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to accept invitation');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" data-testid="invite-loading">
        <Loader2 className="w-8 h-8 text-[#00A3A1] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4" data-testid="invite-error">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-7 h-7 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Invitation Invalid</h1>
          <p className="text-sm text-gray-500 mb-6">{error}</p>
          <Button onClick={() => navigate('/login')} variant="outline" data-testid="go-login-btn">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4" data-testid="invite-success">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-7 h-7 text-green-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Welcome to the team!</h1>
          <p className="text-sm text-gray-500 mb-6">
            You're all set up and signed in. Taking you to your dashboard…
          </p>
          <Button onClick={() => navigate('/dashboard', { replace: true })} className="text-white" style={{ background: '#00A3A1' }}
            data-testid="go-dashboard-after-accept">
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const roleCfg = ROLE_CONFIG[invite.org_role] || ROLE_CONFIG.member;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4" data-testid="accept-invite-page">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#00A3A1] to-[#00A3A1]/80 px-8 py-6 text-white">
          <h1 className="text-xl font-bold mb-1">Join {invite.organization?.name || 'the team'}</h1>
          <p className="text-sm text-white/80">
            {invite.invited_by_name} invited you as
            <span className="inline-flex items-center gap-1 ml-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-white/20">
              {React.createElement(roleCfg.icon, { className: 'w-3 h-3' })}
              {roleCfg.label}
            </span>
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleAccept} className="px-8 py-6 space-y-4">
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Email</label>
            <Input value={invite.email} disabled className="mt-1 bg-gray-50 text-gray-500" data-testid="invite-accept-email" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Full Name</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your full name"
              className="mt-1" required data-testid="invite-accept-name" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Password</label>
            <div className="relative mt-1">
              <Input type={showPass ? 'text' : 'password'} value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Min 8 characters" required minLength={8}
                data-testid="invite-accept-password" />
              <button type="button" onClick={() => setShowPass(!showPass)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Confirm Password</label>
            <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Re-enter password" required className="mt-1" data-testid="invite-accept-confirm" />
          </div>
          <Button type="submit" className="w-full text-white mt-2" style={{ background: '#00A3A1' }}
            disabled={submitting || !name.trim() || !password || !confirmPassword}
            data-testid="accept-invite-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {submitting ? 'Creating Account...' : 'Create Account & Join'}
          </Button>
          <p className="text-center text-xs text-gray-400 mt-3">
            Already have an account?{' '}
            <button type="button" onClick={() => navigate('/login')} className="text-[#00A3A1] hover:underline font-medium">
              Sign in
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}
